import { useEffect, useState } from 'react';
import { PushNotifications, Token, ActionPerformed, PushNotificationSchema } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    // Push notifications only work on native platforms
    const supported = Capacitor.isNativePlatform();
    setIsSupported(supported);

    if (!supported) return;

    initializePushNotifications();
  }, []);

  const initializePushNotifications = async () => {
    try {
      // Request permission
      let permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        throw new Error('User denied push notification permissions');
      }

      // Register with OS
      await PushNotifications.register();

      // Listen for registration
      await PushNotifications.addListener('registration', async (token: Token) => {
        setToken(token.value);
        await savePushToken(token.value);
      });

      // Listen for registration errors
      await PushNotifications.addListener('registrationError', () => {
        toast({
          title: 'Push Notification Error',
          description: 'Failed to register for push notifications',
          variant: 'destructive',
        });
      });

      // Listen for push notifications received while app is open
      await PushNotifications.addListener(
        'pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          // Show toast for foreground notifications
          toast({
            title: notification.title || 'Notification',
            description: notification.body || '',
          });
        }
      );

      // Listen for user tapping on notification
      await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (notification: ActionPerformed) => {
          // Handle navigation based on notification data
          const data = notification.notification.data;
          if (data?.action_route) {
            // Navigation can be handled via router if needed
          }
        }
      );

    } catch {
      // Silent error - push notifications are optional
    }
  };

  const savePushToken = async (pushToken: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Save token to user's notification preferences
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          push_token: pushToken,
          push_enabled: true,
        });

      if (error) throw error;
    } catch {
      // Silent error - push token saving is optional
    }
  };

  const requestPermission = async () => {
    if (!isSupported) {
      toast({
        title: 'Not Supported',
        description: 'Push notifications are only available on mobile devices',
        variant: 'destructive',
      });
      return false;
    }

    try {
      const permStatus = await PushNotifications.requestPermissions();
      if (permStatus.receive === 'granted') {
        await PushNotifications.register();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const disablePushNotifications = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('notification_preferences')
        .update({ push_enabled: false })
        .eq('user_id', user.id);

      // Unregister from push notifications
      if (isSupported) {
        await PushNotifications.removeAllListeners();
      }
    } catch {
      // Silent error - disabling push notifications is optional
    }
  };

  return {
    isSupported,
    token,
    requestPermission,
    disablePushNotifications,
  };
};
